import { useState, useRef, useCallback, useEffect } from "react";
import { kaiClient } from "./kaiClient";

/**
 * Voice for KAI - fully server-driven so it works even when the network blocks
 * Google (which breaks both Chrome's recognizer and its English voices):
 *  - Speech-to-text: MediaRecorder → gateway (Whisper).
 *  - Text-to-speech: gateway (OpenAI) → played via the Web Audio API, which
 *    bypasses the <audio> autoplay block. The AudioContext is unlocked on the
 *    Talk gesture so the spoken reply is allowed to play.
 *  - Falls back to the browser voice if server TTS ever fails.
 */
const STT_SUPPORTED =
  typeof navigator !== "undefined" &&
  !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) &&
  typeof window !== "undefined" &&
  typeof window.MediaRecorder !== "undefined";

const AudioCtx =
  typeof window !== "undefined" ? window.AudioContext || window.webkitAudioContext : null;
const TTS_SUPPORTED =
  !!AudioCtx ||
  (typeof window !== "undefined" &&
    !!window.speechSynthesis &&
    typeof window.SpeechSynthesisUtterance !== "undefined");

const VOICE_LS_KEY = "kai-voice-name";

// OpenAI TTS voices (nova/shimmer are female).
const OPENAI_VOICES = [
  { name: "nova", label: "Nova (female, warm)" },
  { name: "shimmer", label: "Shimmer (female, soft)" },
  { name: "fable", label: "Fable (expressive)" },
  { name: "alloy", label: "Alloy (neutral)" },
  { name: "echo", label: "Echo (male)" },
  { name: "onyx", label: "Onyx (male, deep)" },
];
const VALID_VOICE_NAMES = OPENAI_VOICES.map((v) => v.name);

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function base64ToArrayBuffer(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export function useVoice({ onTranscript, onError } = {}) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [voiceName, setVoiceName] = useState(() => {
    try {
      const saved = localStorage.getItem(VOICE_LS_KEY);
      // Ignore a stale browser-voice name from the previous version.
      return saved && VALID_VOICE_NAMES.includes(saved) ? saved : "nova";
    } catch {
      return "nova";
    }
  });

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const sourceRef = useRef(null);
  const voiceNameRef = useRef(voiceName);
  voiceNameRef.current = voiceName;
  const onTranscriptRef = useRef(onTranscript);
  const onErrorRef = useRef(onError);
  onTranscriptRef.current = onTranscript;
  onErrorRef.current = onError;

  const ensureCtx = useCallback(() => {
    if (!AudioCtx) return null;
    if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
    return audioCtxRef.current;
  }, []);

  // Unlock audio playback within a user gesture (so the async reply can play).
  const unlock = useCallback(() => {
    const ctx = ensureCtx();
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
  }, [ensureCtx]);

  const speakWithBrowser = useCallback((text) => {
    try {
      if (!window.speechSynthesis || typeof window.SpeechSynthesisUtterance === "undefined") return;
      window.speechSynthesis.cancel();
      const u = new window.SpeechSynthesisUtterance(text);
      u.lang = "en-GB";
      u.onend = () => setSpeaking(false);
      u.onerror = () => setSpeaking(false);
      setSpeaking(true);
      window.speechSynthesis.speak(u);
    } catch {
      setSpeaking(false);
    }
  }, []);

  const stopSpeaking = useCallback(() => {
    try {
      if (sourceRef.current) {
        sourceRef.current.onended = null;
        sourceRef.current.stop();
        sourceRef.current = null;
      }
    } catch {
      /* ignore */
    }
    try {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    } catch {
      /* ignore */
    }
    setSpeaking(false);
  }, []);

  const speak = useCallback(
    async (text) => {
      const t = (text || "").trim();
      if (!t) return;
      stopSpeaking();
      try {
        const { audioBase64 } = await kaiClient.speak(t, voiceNameRef.current);
        const ctx = ensureCtx();
        if (!audioBase64 || !ctx) {
          speakWithBrowser(t);
          return;
        }
        if (ctx.state === "suspended") {
          try {
            await ctx.resume();
          } catch {
            /* ignore */
          }
        }
        const buffer = await ctx.decodeAudioData(base64ToArrayBuffer(audioBase64));
        const src = ctx.createBufferSource();
        src.buffer = buffer;
        src.connect(ctx.destination);
        src.onended = () => setSpeaking(false);
        sourceRef.current = src;
        setSpeaking(true);
        src.start(0);
      } catch {
        speakWithBrowser(t);
      }
    },
    [ensureCtx, speakWithBrowser, stopSpeaking]
  );

  const selectVoice = useCallback(
    (name) => {
      setVoiceName(name);
      voiceNameRef.current = name;
      try {
        localStorage.setItem(VOICE_LS_KEY, name);
      } catch {
        /* ignore */
      }
      unlock();
      speak("Hi, this is your assistant voice.");
    },
    [unlock, speak]
  );

  // ── Speech-to-text (record → server transcribe) ──
  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (!STT_SUPPORTED || recorderRef.current) return;
    unlock(); // gesture: enable audio playback for the upcoming reply
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      if (onErrorRef.current) onErrorRef.current({ status: 403, code: "not-allowed" });
      return;
    }
    streamRef.current = stream;
    const preferred = "audio/webm";
    const recorder =
      window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported(preferred)
        ? new window.MediaRecorder(stream, { mimeType: preferred })
        : new window.MediaRecorder(stream);
    chunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = async () => {
      stopStream();
      const mimeType = recorder.mimeType || "audio/webm";
      const blob = new Blob(chunksRef.current, { type: mimeType });
      chunksRef.current = [];
      recorderRef.current = null;
      if (!blob.size) return;
      setTranscribing(true);
      try {
        const audioBase64 = await blobToBase64(blob);
        const { text } = await kaiClient.transcribe(audioBase64, mimeType);
        const clean = (text || "").trim();
        if (clean && onTranscriptRef.current) onTranscriptRef.current(clean);
        else if (!clean && onErrorRef.current) onErrorRef.current({ code: "no-speech" });
      } catch (err) {
        if (onErrorRef.current) onErrorRef.current(err);
      } finally {
        setTranscribing(false);
      }
    };
    recorder.start();
    recorderRef.current = recorder;
    setRecording(true);
  }, [stopStream, unlock]);

  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      try {
        recorder.stop();
      } catch {
        /* ignore */
      }
    }
    setRecording(false);
  }, []);

  useEffect(() => {
    return () => {
      try {
        const recorder = recorderRef.current;
        if (recorder && recorder.state !== "inactive") recorder.stop();
      } catch {
        /* ignore */
      }
      stopStream();
      try {
        if (sourceRef.current) sourceRef.current.stop();
      } catch {
        /* ignore */
      }
      try {
        if (window.speechSynthesis) window.speechSynthesis.cancel();
      } catch {
        /* ignore */
      }
    };
  }, [stopStream]);

  return {
    sttSupported: STT_SUPPORTED,
    ttsSupported: TTS_SUPPORTED,
    supported: STT_SUPPORTED || TTS_SUPPORTED,
    recording,
    transcribing,
    speaking,
    voices: OPENAI_VOICES,
    voiceName,
    selectVoice,
    speak,
    stopSpeaking,
    startRecording,
    stopRecording,
    unlock,
  };
}
